<template>
  <div class="d-inline-block">
    <v-menu 
      v-if="visible"
      offset-y 
      transition="slide-y-transition"
      rounded="lg"
      elevation="8"
      min-width="290"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          color="primary"
          rounded
          elevation="2"
          :class="[buttonClass, 'px-4 font-weight-bold white--text']"
          v-bind="attrs"
          v-on="on"
          :loading="isLoading"
        >
          <v-icon left size="20">mdi-cloud-download-outline</v-icon>
          <span>ΕΞΑΓΩΓΗ</span>
          <v-icon right size="18" class="ml-1">mdi-chevron-down</v-icon>
        </v-btn>
      </template>

      <v-card flat>
        <v-list dense class="py-1">
          <v-subheader class="text-uppercase font-weight-bold text-caption grey--text text--darken-1 px-4 py-0" style="height: 28px;">
            Επιλογές Εξαγωγής
          </v-subheader>

          <v-list-item link @click="handleExcel" class="px-3 py-1">
            <v-list-item-avatar size="36" color="green lighten-5" class="my-1 mr-3">
              <v-icon color="green darken-1" size="22">mdi-file-excel</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium text-body-2">
                1. Εξαγωγή σε Excel
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption grey--text">
                Αρχείο .xlsx
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="my-0">
              <v-icon small color="grey lighten-1">mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>

          <v-divider class="my-1 mx-2"></v-divider>

          <v-list-item link @click="handlePdf" class="px-3 py-1">
            <v-list-item-avatar size="36" color="red lighten-5" class="my-1 mr-3">
              <v-icon color="red darken-1" size="22">mdi-file-pdf-box</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium text-body-2">
                2. Εξαγωγή σε PDF
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption grey--text">
                Εκτυπώσιμη αναφορά (.pdf)
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="my-0">
              <v-icon small color="grey lighten-1">mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>

          <v-divider class="my-1 mx-2"></v-divider>

          <v-list-item link @click="openMailDialog" class="px-3 py-1">
            <v-list-item-avatar size="36" color="blue lighten-5" class="my-1 mr-3">
              <v-icon color="blue darken-2" size="22">mdi-email-send-outline</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium text-body-2">
                3. Αποστολή με Email
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption grey--text">
                Αποστολή αναφοράς σε email
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="my-0">
              <v-icon small color="grey lighten-1">mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>

    <!-- Email Dialog -->
    <v-dialog v-model="dialogMail" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">Αποστολή αναφοράς με email</v-card-title>
        <v-card-text>
          <v-text-field 
            v-model="mailRecipientAddress"
            label="Διεύθυνση αποστολής"
            hint="Για πολλαπλά email διαχωρίστε με ; (π.χ. email1@test.gr; email2@test.gr)"
            persistent-hint>
          </v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary darken-1" text @click="dialogMail = false">ΑΚΥΡΟ</v-btn>
          <v-btn color="success darken-1" @click="mailConfirm" :loading="mailLoading">ΑΠΟΣΤΟΛΗ</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'TableExportMenu',
  props: {
    title: {
      type: String,
      default: 'Αναφορά'
    },
    filename: {
      type: String,
      default: ''
    },
    headers: {
      type: Array,
      default: () => []
    },
    items: {
      type: Array,
      default: () => []
    },
    filter: {
      type: Function,
      default: null
    },
    visible: {
      type: Boolean,
      default: true
    },
    buttonClass: {
      type: String,
      default: 'm-2'
    },
    loading: {
      type: Boolean,
      default: false
    },
    emailAddress: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      internalLoading: false,
      dialogMail: false,
      mailLoading: false,
      mailRecipientAddress: ''
    }
  },

  computed: {
    isLoading() {
      return this.loading || this.internalLoading;
    },

    cleanFilename() {
      const base = this.filename || this.title || 'αναφορά';
      return base.replace(/[^\w\u0370-\u03FF\-_]/g, '_');
    }
  },

  watch: {
    emailAddress: {
      immediate: true,
      handler(val) {
        if (val) {
          this.mailRecipientAddress = val;
        }
      }
    }
  },

  methods: {
    getTablePayload() {
      const exportableColumns = (this.headers || []).filter(x => (x.exportable ?? true) === true && x.value !== 'actions');
      const columnDefinitions = exportableColumns.map(x => ({ propertyName: x.value, exportTransformer: x.exportTransformer }));

      let rows = this.items || [];
      if (typeof this.filter === 'function') {
        rows = rows.filter(this.filter);
      }

      const tableEntries = rows.map(line => {
        return columnDefinitions.map(col => {
          const val = line[col.propertyName];
          if (col.exportTransformer && val != null) {
            return col.exportTransformer(val);
          }
          return val != null ? String(val) : '';
        });
      });

      return {
        title: this.title || 'Αναφορά',
        table_headers: exportableColumns.map(x => x.text),
        table_entries: tableEntries
      };
    },

    handleExcel() {
      if (this.$listeners.excel) {
        this.$emit('excel');
        return;
      }
      const payload = this.getTablePayload();
      this.axiosDownload('/api/test', payload, `${this.cleanFilename}.xlsx`);
    },

    handlePdf() {
      if (this.$listeners.pdf) {
        this.$emit('pdf');
        return;
      }
      const payload = this.getTablePayload();
      this.axiosDownload('/api/export/pdf', payload, `${this.cleanFilename}.pdf`);
    },

    openMailDialog() {
      if (this.$listeners.mail) {
        this.$emit('mail');
        return;
      }
      if (this.emailAddress) {
        this.mailRecipientAddress = this.emailAddress;
      }
      this.dialogMail = true;
    },

    mailConfirm() {
      if (!this.mailRecipientAddress || !this.mailRecipientAddress.trim()) {
        if (this.$swal) {
          this.$swal.fire('Προσοχή', 'Παρακαλώ εισάγετε τουλάχιστον μία διεύθυνση email.', 'warning');
        }
        return;
      }

      this.mailLoading = true;
      const payload = this.getTablePayload();

      axios.post('/api/export/mail', {
        ...payload,
        email_address: this.mailRecipientAddress
      }).then(response => {
        if (this.$swal) {
          this.$swal.fire('Επιτυχία', response.data.message || 'Η αναφορά απεστάλη επιτυχώς με email.', 'success');
        }
        this.dialogMail = false;
      }).catch(error => {
        console.error(error);
        let msg = 'Σφάλμα κατά την αποστολή του email.';
        if (error.response && error.response.data && error.response.data.message) {
          msg = error.response.data.message;
        }
        if (this.$swal) {
          this.$swal.fire('Σφάλμα', msg, 'error');
        }
      }).finally(() => {
        this.mailLoading = false;
      });
    },

    axiosDownload(url, data, filename) {
      this.internalLoading = true;
      axios({
        url: url,
        data: data,
        method: 'POST',
        responseType: 'blob'
      }).then(response => {
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      }).catch(err => {
        console.error(err);
        if (this.$swal) {
          this.$swal.fire('Σφάλμα', 'Παρουσιάστηκε σφάλμα κατά τη λήψη του αρχείου.', 'error');
        }
      }).finally(() => {
        this.internalLoading = false;
      });
    }
  }
}
</script>
